import { z } from "zod";
import { Actor, Surface } from "./actor.js";
/** What a face (or EmmA) POSTs to the backbone: `POST /propose`. */
export const Proposal = z.object({
    actor: Actor,
    surface: Surface,
    /** A name registered in packages/commands, e.g. `create_lead`. */
    command: z.string().regex(/^[a-z][a-z0-9_]*$/, "snake_case command name"),
    args: z.record(z.string(), z.unknown()),
    /** A held case whose requested changes this fresh proposal addresses. */
    revision_of: z.string().uuid().nullable().optional(),
    /** Caller-chosen; the backbone returns the same action for a repeat. */
    idempotency_key: z.string().min(8).max(200),
    /** emma_runs.id when an agent proposes — answers "why now" with a link. */
    run_id: z.string().uuid().nullable().default(null),
    /** Staff sessions carrying an explicit test-set header; the backbone sets
     *  `aiw.test_set_id` for the transaction and marks every row `test`. */
    test_set_id: z.string().uuid().nullable().default(null),
    /** ADR-024: the click that proposes this IS the human verdict. Only a signed-in staff member's own
     *  request may carry one (never an agent, never EmmA's instructed path). The backbone still opens the
     *  case, records it decided by the proposer and executes, all in one transaction, so nobody is asked
     *  twice. ADR-027: absent on a signed-in staff request, the backbone treats it as present — a staff
     *  member's own request is its approval. Only an agent's proposal waits as a case. */
    verdict: z.object({ decision: z.literal("approve"), reason: z.string().max(4000).nullable().default(null) }).optional(),
});
/** The short request number (bb.action.request_no, 4 Nov 2026) — what the card shows as #1042. */
const RequestNo = z.number().int().positive().nullable().optional();
/** The backbone's answer, including a recorded request for changes. */
export const ProposalResult = z.discriminatedUnion("status", [
    z.object({ status: z.literal("revision_requested"), action_id: z.string().uuid(), request_no: RequestNo, case_id: z.string().uuid(), reason: z.string() }),
    z.object({
        status: z.literal("executed"),
        action_id: z.string().uuid(),
        request_no: RequestNo,
        result: z.unknown(),
        /** Present when the click carried its own verdict (ADR-024): the audited case it decided. */
        case_id: z.string().uuid().optional(),
    }),
    z.object({
        status: z.literal("pending"),
        action_id: z.string().uuid(),
        request_no: RequestNo,
        case_id: z.string().uuid(),
    }),
    z.object({
        status: z.literal("denied"),
        action_id: z.string().uuid(),
        request_no: RequestNo,
        reason: z.string(),
    }),
]);
//# sourceMappingURL=proposal.js.map